import { Trans } from 'next-i18next'

import {
  Heading1,
  Heading3,
  LinkGrid,
  LinkStack,
  LinkText,
  Paragraph,
  Section1,
  Section2
} from '@/features/Listings/seo/elements'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import { toTitleCase } from '@/features/Listings/seo/search-results-content/content/utils'
import { Label } from '@/components/text'

const PopularFilters = ({
  isCanadaPage,
  citySlug,
  cityName,
  cityNameWithState,
  hasFilter,
  filterName,
  customContent
}: {
  isCanadaPage: boolean
  citySlug: string
  cityName: string
  cityNameWithState: string
  hasFilter: boolean
  filterName: string
  customContent?: React.ReactNode
}) => {
  const links = getFilterLinks(isCanadaPage, citySlug)

  const rentalCategories = [
    {
      headingKey: 'seo:LOCATION Rental Types',
      headingDefault: '{{ location }} Rental Types',
      links: [
        {
          key: 'seo:Apartments for rent LOCATION',
          default: 'Apartments for rent {{ location }}',
          href: links.apartment
        },
        {
          key: 'seo:Condos for rent LOCATION',
          default: 'Condos for rent {{ location }}',
          href: links.apartment
        },
        {
          key: 'seo:Townhouses for rent LOCATION',
          default: 'Townhouses for rent {{ location }}',
          href: links.townhouse
        },
        {
          key: 'seo:Houses for rent LOCATION',
          default: 'Houses for rent {{ location }}',
          href: links.house
        },
        {
          key: 'seo:Rooms for rent LOCATION',
          default: 'Rooms for rent {{ location }}',
          href: links.room
        }
      ]
    },
    {
      headingKey: 'seo:LOCATION Rentals by Bedrooms',
      headingDefault: '{{ location }} Rentals by Bedrooms',
      links: [
        {
          key: 'seo:Studios for rent LOCATION',
          default: 'Studios for rent {{ location }}',
          href: links.studio
        },
        {
          key: 'seo:1 bedrooms for rent LOCATION',
          default: '1 bedrooms for rent {{ location }}',
          href: links.bed1
        },
        {
          key: 'seo:2 bedrooms for rent LOCATION',
          default: '2 bedrooms for rent {{ location }}',
          href: links.bed2
        },
        {
          key: 'seo:3 bedrooms for rent LOCATION',
          default: '3 bedrooms for rent {{ location }}',
          href: links.bed3
        },
        {
          key: 'seo:4+ bedrooms for rent LOCATION',
          default: '4+ bedrooms for rent {{ location }}',
          href: links.bed4
        }
      ]
    },
    {
      headingKey: 'seo:LOCATION Rentals by Amenity',
      headingDefault: '{{ location }} Rentals by Amenity',
      links: [
        {
          key: 'seo:Furnished rentals LOCATION',
          default: 'Furnished rentals {{ location }}',
          href: links.furnished
        },
        {
          key: 'seo:Unfurnished rentals LOCATION',
          default: 'Unfurnished rentals {{ location }}',
          href: links.unfurnished
        },
        {
          key: 'seo:Pet-friendly rentals LOCATION',
          default: 'Pet-friendly rentals {{ location }}',
          href: links.petFriendly
        },
        {
          key: 'seo:Dog-friendly rentals LOCATION',
          default: 'Dog-friendly rentals {{ location }}',
          href: links.dogFriendly
        },
        {
          key: 'seo:Cat-friendly rentals LOCATION',
          default: 'Cat-friendly rentals {{ location }}',
          href: links.catFriendly
        }
      ]
    },
    {
      headingKey: 'seo:LOCATION Rentals by Lease',
      headingDefault: '{{ location }} Rentals by Lease',
      links: [
        {
          key: 'seo:Long term rentals LOCATION',
          default: 'Long term rentals {{ location }}',
          href: links.longTerm
        },
        {
          key: 'seo:Short term rentals LOCATION',
          default: 'Short term rentals {{ location }}',
          href: links.shortTerm
        },
        {
          key: 'seo:Month-to-month rentals LOCATION',
          default: 'Month-to-month rentals {{ location }}',
          href: links.monthToMonth
        },
        {
          key: 'seo:Fixed term rentals LOCATION',
          default: 'Fixed term rentals {{ location }}',
          href: links.fixedTerm
        }
      ]
    },
    {
      headingKey: 'seo:LOCATION Rentals by Price',
      headingDefault: '{{ location }} Rentals by Price',
      links: [
        {
          key: 'seo:LOCATION apartments under $1,000',
          default: '{{ location }} apartments under $1,000',
          href: links.under1000
        },
        {
          key: 'seo:LOCATION apartments under $1,500',
          default: '{{ location }} apartments under $1,500',
          href: links.under1500
        },
        {
          key: 'seo:LOCATION apartments under $2,000',
          default: '{{ location }} apartments under $2,000',
          href: links.under2000
        },
        {
          key: 'seo:LOCATION apartments under $2,500',
          default: '{{ location }} apartments under $2,500',
          href: links.under2500
        }
      ]
    }
  ]

  return (
    <Section1>
      <Section2>
        <Heading1>
          {hasFilter ? (
            <Trans
              i18nKey="seo:Looking for FILTER in LOCATION?"
              values={{
                filter: toTitleCase(filterName),
                location: cityNameWithState
              }}
              defaults="Looking for {{ filter }} in {{ location }}?"
            />
          ) : (
            <Trans
              i18nKey="seo:Looking for Apartments in LOCATION?"
              values={{
                location: cityNameWithState
              }}
              defaults="Looking for Apartments in {{ location }}?"
            />
          )}
        </Heading1>
        {customContent || (
          <Paragraph>
            <Trans
              i18nKey="seo:Looking for Apartments in LOCATION? Paragraph"
              values={{
                location: cityNameWithState
              }}
              defaults="Looking for a specific type of rental in {{ location }}? Use our filters below to find your next home."
            />
          </Paragraph>
        )}
      </Section2>
      <LinkGrid>
        {rentalCategories.map((category, index) => (
          <LinkStack key={index}>
            <Heading3>
              <Trans
                i18nKey={category.headingKey}
                values={{ location: cityName }}
                defaults={category.headingDefault}
              />
            </Heading3>
            {category.links.map((link, idx) => (
              <Label key={idx} fontSize="14px">
                <LinkText href={link.href}>
                  <Trans
                    i18nKey={link.key}
                    values={{ location: cityName }}
                    defaults={link.default}
                  />
                </LinkText>
              </Label>
            ))}
          </LinkStack>
        ))}
      </LinkGrid>
    </Section1>
  )
}

export default PopularFilters
